/**
 * The level widgets (designs/Caged Fighter Level, "Home · Level"), trimmed to what a player needs
 * at a glance: level and XP bar with the thinner skill point bar, and the weekly full-XP fights with
 * the rested chip. The weekly bands are explained by the popup when a player reaches one.
 */
import { PROGRESSION, type Progress } from '@caged/shared';
import { Trans, useTranslation } from 'react-i18next';
import { countdown, formatNumber } from '../format';
import { levelView, skillBarView, streakView, weeklyView } from '../progression';
import { SpBar, XpBar } from './Bars';
import s from './ProgressWidgets.module.css';
import ui from './ui.module.css';

type Size = 'md' | 'lg';

/** "3d 4h" / "5h 12m" / "12m". */
export function useDuration() {
  const { t } = useTranslation();
  return (ms: number) => {
    const c = countdown(ms);
    if (c.days > 0) return t('screens.time.dh', { d: c.days, h: c.hours });
    if (c.hours > 0) return t('screens.time.hm', { h: c.hours, m: c.minutes });
    return t('screens.time.m', { m: Math.max(1, c.minutes) });
  };
}

/** The amber level square; small ones (under 40 px) show only the number. */
export function LevelBadge({ level, size = 46 }: { level: number; size?: number }) {
  const { t } = useTranslation();
  const compact = size < 40;
  return (
    <div
      className={s.badge}
      style={{ width: size, height: size, borderRadius: size * 0.3, boxShadow: compact ? '0 3px 0 var(--amber-deep)' : undefined }}
      aria-label={t('screens.level.level', { level })}
    >
      {!compact && (
        <span className={s.badgeLabel} style={{ fontSize: Math.max(8, size * 0.17) }} aria-hidden="true">
          {t('screens.level.lvl')}
        </span>
      )}
      <span className={s.badgeNum} style={{ fontSize: compact ? size * 0.47 : size * 0.48 }} aria-hidden="true">
        {level}
      </span>
    </div>
  );
}

/** "NEXT SKILL POINT · 80 / 604 XP". */
export function SkillPointLine({ pointsEarned, barXp }: { pointsEarned: number; barXp: number }) {
  const sp = skillBarView(pointsEarned, barXp);
  return (
    <div className={s.spRow}>
      <span className={s.text}>
        <Trans
          i18nKey={sp.capped ? 'screens.level.nextCoins' : 'screens.level.nextPoint'}
          values={{ now: formatNumber(sp.now), need: formatNumber(sp.need), coins: PROGRESSION.skillPoints.overflowCoins }}
          components={{ g: <span className={s.green} /> }}
        />
      </span>
    </div>
  );
}

/** Days in a row with a fight; the reward for a full streak shows on hover. */
export function StreakChip({ progress, now }: { progress: Progress; now: number }) {
  const { t } = useTranslation();
  const st = streakView(progress, now);
  return (
    <span className={[s.streak, s.chipEnd, st.days === 0 ? s.streakOff : ''].join(' ')} title={t('screens.level.streakNext', { xp: st.reward, days: st.target })}>
      {st.days > 0 ? t('screens.level.streak', { count: st.days }) : t('screens.level.streakNone')}
    </span>
  );
}

export function LevelCard({
  progress,
  now,
  size = 'md',
  streak = true,
  className,
}: {
  progress: Progress;
  now: number;
  size?: Size;
  streak?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const lv = levelView(progress.xpTotal);
  const sp = skillBarView(progress.skillPointsEarned, progress.skillBarXp);
  const lg = size === 'lg';
  return (
    <section className={[ui.card, s.levelCard, lg ? s.lg : '', className ?? ''].join(' ')} aria-label={t('screens.level.title')}>
      {lg && (
        <div className={s.row}>
          <span className={ui.label}>{t('screens.level.title')}</span>
          {streak && <StreakChip progress={progress} now={now} />}
        </div>
      )}
      <div className={s.group}>
        <div className={s.row}>
          <div className={s.levelHead}>
            <LevelBadge level={lv.level} size={lg ? 64 : 46} />
            <div className={s.levelText}>
              <span className={s.levelTitle}>{t('screens.level.level', { level: lv.level })}</span>
              <span className={s.levelXp}>
                {lv.maxed
                  ? t('screens.level.max')
                  : t('screens.level.xp', { now: formatNumber(lv.into), need: formatNumber(lv.needed) })}
              </span>
            </div>
          </div>
          {!lg && streak && <StreakChip progress={progress} now={now} />}
        </div>
        <XpBar value={lv.frac} height={lg ? 10 : 8} label={t('screens.level.title')} />
      </div>
      <div className={s.spBlock}>
        <SkillPointLine pointsEarned={progress.skillPointsEarned} barXp={progress.skillBarXp} />
        <SpBar value={sp.frac} height={4} label={t('screens.level.spLabel')} />
      </div>
    </section>
  );
}

export function WeeklyCard({ progress, now, size = 'md', className }: { progress: Progress; now: number; size?: Size; className?: string }) {
  const { t } = useTranslation();
  const duration = useDuration();
  const w = weeklyView(progress, now);
  const rested = w.restedLeft > 0;
  const lg = size === 'lg';
  const full = w.fights < w.fullXpFights;
  const chip = rested ? (
    <span className={[ui.chipGreen, s.chipEnd, lg ? s.chipLg : ''].join(' ')}>
      {t('screens.weekly.rested', { count: w.restedLeft, factor: PROGRESSION.rested.factor })}
    </span>
  ) : null;
  const title = (
    <span className={s.weeklyTitle}>
      {full
        ? t('screens.weekly.fullXp', { count: w.fights, max: w.fullXpFights })
        : t('screens.weekly.overBudget', { count: w.fights, pct: Math.round(w.nextFactor * 100) })}
    </span>
  );
  return (
    <section
      className={[ui.card, s.weekly, rested ? s.weeklyRested : '', lg ? s.lg : '', className ?? ''].join(' ')}
      aria-label={t('screens.weekly.title')}
    >
      {lg && (
        <div className={s.row}>
          <span className={ui.label}>{t('screens.weekly.title')}</span>
          {chip}
        </div>
      )}
      <div className={s.group}>
        {lg ? (
          title
        ) : (
          <div className={s.row}>
            <div className={s.weeklyHead}>
              <span className={ui.label}>{t('screens.weekly.title')}</span>
              {title}
            </div>
            {chip}
          </div>
        )}
        <XpBar value={Math.min(1, w.fights / w.fullXpFights)} height={lg ? 10 : 8} label={t('screens.weekly.title')} />
      </div>
      <span className={[s.small, s.weeklyFoot].join(' ')}>{t('screens.weekly.resetsIn', { time: duration(w.resetAt - now) })}</span>
    </section>
  );
}
